<script setup lang="ts">
import { ref } from 'vue'
import type { RouteParams } from 'vue-router'
import { NovaModalDialog } from '#components'
// import { bridgeToApp } from '@bridge'

const { gtEvent } = useGoogleTag()
const show = ref(false)
const { t } = useI18n()
const { show: modalShow, hide: modalHide } = useModal()
// const appStore = useAppStore()
const uuid = ref<string>('')
const chatbotServiceController = ref<Nullable<AbortController>>(null)

// 시작 유효 아이디 api
// 모달 오픈시 웰컴 메시지용 전달 api ,토큰 사용량 오버 시 에러코드 api
// 팝업 열릴때 호출하는 쪽에서 파라미터 받아 모델 업데이트
const handleOnGetParams = (evt: RouteParams) => {
  uuid.value = evt.value as unknown as string
  chatbotServiceController.value = new AbortController()
}

const handleOnOutside = async () => {
  await modalShow({
    component: NovaModalDialog,
    bind: {
      name: modalsName.MODAL_DIALOG,
      contentsTextAlign: 'center',
      btns: [
        {
          label: t('cancel'),
          theme: 'secondary-gray-200',
          size: 32,
          onClick: async () => {
            gtEvent('clickEvent', {
              eventCategory: '클릭',
              eventAction: '챗봇 모달 닫기 취소',
              eventLabel: '취소',
              eventSlot: '챗봇 모달 닫기 다이얼로그',
              eventI18nAddr: useKoreanTranslation('cancel'),
              eventComponent: 'NovaModalDialog',
            })
            await modalHide(modalsName.MODAL_DIALOG)
          },
        },
        {
          label: t('confirm'),
          theme: 'primary-blue-dark',
          size: 32,
          onClick: async () => {
            gtEvent('clickEvent', {
              eventCategory: '클릭',
              eventAction: '챗봇 모달 닫기 컨펌',
              eventLabel: '닫기',
              eventSlot: '챗봇 모달 닫기 다이얼로그',
              eventI18nAddr: useKoreanTranslation('confirm'),
              eventComponent: 'NovaModalDialog',
            })
            await modalHide(modalsName.MODAL_DIALOG)
            await modalHide(modalsName.MODAL_CHATBOT)
          },
        },
      ],
    },
    slots: {
      title: t('chatBot.dialog.title'),
      content: t('chatBot.dialog.message'),
    },
  })
}

// watch(
//   () => show.value,
//   (cur) => {
//     if (appStore.appType !== 'AOS') return
//
//     if (cur) {
//       try {
//         bridgeToApp.setAosKeyboardMode({ mode: 'adjustResize' })
//       } catch (err) {
//         console.error(err)
//       }
//     } else {
//       try {
//         bridgeToApp.setAosKeyboardMode({ mode: 'adjustResize' })
//       } catch (err) {
//         console.error(err)
//       }
//     }
//   }
// )
</script>

<template>
  <VueFinalModal
    v-model="show"
    :name="modalsName.MODAL_CHATBOT"
    :content-class="'modal-content'"
    :click-to-close="false"
    classes="modal-container"
    @before-open="(evt: any) => handleOnGetParams(evt.ref.params)"
    @click-outside="handleOnOutside"
  >
    <ClientOnly>
      <NovaBoxBase v-if="show" class="modal-box">
        <div class="modal-header">
          <div class="title">
            <span class="logo">
              <img
                src="@assets/images/logo-horizon-on-light.svg?url"
                alt="I LIKE LM"
                title="I LIKE LM"
              />
            </span>
            <span class="label">BETA</span>
          </div>
          <NovaButtonIcon
            :icon="{ type: 'outline', name: 'close-extend' }"
            :theme="'transparent'"
            :size="24"
            class="btn-close"
            @click="handleOnOutside"
          />
        </div>
        <NovaChatbot v-if="uuid" :render-type="'modal'" :uuid="uuid" />
      </NovaBoxBase>
    </ClientOnly>
  </VueFinalModal>
</template>

<style lang="scss" scoped>
:deep(.modal-container) {
  display: flex;
  justify-content: center;
  align-items: center;
}

:deep(.vfm--overlay) {
  background-color: hex-to-rgba($color-black, 0.65);
}

:deep(.modal-content) {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: calc(30px + 24px + 12px) 32px 32px;
  width: 100%;
  height: 100%;
  pointer-events: none;

  .modal-box {
    width: 100%;
    padding: 0;
    max-width: 1024px;
    height: 100%;
    min-height: 360px;
    max-height: 740px;
    border-radius: 20px !important;
    pointer-events: auto;
    overflow: hidden;
    .modal-header {
      position: relative;
      padding: 24px;
      @include mobile {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        padding: 10px 20px;
      }

      .title {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: 12px;

        .logo {
          display: inline-block;
          width: 80px;
          @include mobile {
            width: 72px;
          }
        }

        .label {
          display: inline-block;
          padding: 0 8px;
          @include text-style($text-body-14-regular);
          color: $color-blue-900;
          border: 1px solid $color-blue-900;
          border-radius: 12px;
        }
      }

      .btn-close {
        position: absolute;
        top: 30px;
        right: 30px;
        color: $color-black;
        z-index: 50;
        @include mobile {
          top: 18px;
          right: 20px;
        }
      }
    }

    :deep(.modal-body) {
      scroll-behavior: smooth; /* 부드러운 스크롤 */
    }
  }
}

@include mobile {
  :deep(.modal-content) {
    width: 100%;
    height: 100%;
    padding: 0;

    .modal-box {
      width: 100%;
      max-width: 100%;
      height: 100%;
      border-radius: 0 !important;
      max-height: none;

      .modal-header {
        flex-shrink: 0;
        height: 56px;

        .on-mobile {
          display: flex;
        }

        .on-desktop {
          .btn-close {
            display: none;
          }
        }
      }

      .modal-body {
        border-radius: 0;

        > .post {
          max-height: initial;
        }
      }
    }
  }
}
</style>
